import { isArray } from 'lodash';
import {
  APISurveyAnswer,
  APISurveyDependentQuestionAnswer,
  APISurveyParentAnswer,
  APISurveyReferenceAnswer,
  ArdoqId,
  PersistedReferenceQuestion,
  PersistedSurveyQuestion,
  PersistedTagQuestion,
  SurveyQuestionType,
  SurveyQuestionValidator,
} from '@ardoq/api-types';
import { isBefore, parseDate, currentDate } from '@ardoq/date-time';
import {
  Answer,
  AttributeAnswer,
  AttributeAnswerValue,
  ConditionAnswer,
  DateRange,
  FieldAnswerValue,
  ParentAnswer,
  ReferenceAnswer,
  ReferenceAnswerValue,
  TagAnswer,
  TagAnswerValue,
} from '../types';
import {
  isDateRangeQuestion,
  isDateRangeValue,
  isRangeValidator,
  isReferenceQuestion,
  isTagQuestion,
} from '../questions/questionPredicates';

export const isAttributeAnswer = (answer?: Answer): answer is AttributeAnswer =>
  answer === undefined ||
  answer === null ||
  typeof answer.value === 'string' ||
  answer.value === null ||
  answer.value === undefined;

export const isParentAnswer = (answer?: Answer): answer is ParentAnswer =>
  Boolean(answer && 'parentName' in answer);

export const isAnsweredParentQuestion = (
  answer?: Answer
): answer is ParentAnswer => Boolean(answer && 'parentName' in answer);

export const isEmptyAnswer = (
  value?: FieldAnswerValue | ReferenceAnswerValue[] | ArdoqId[]
) =>
  (typeof value === 'string' && value.trim() === '') ||
  value === null ||
  value === undefined ||
  (isArray(value) && value.length !== undefined && value.length === 0);

export const isEmptyDateRangeAnswer = (value?: DateRange) =>
  !value || (!value.startDate && !value.endDate);

const isPartialDateRangeAnswer = (value?: DateRange) =>
  value &&
  ((!value.startDate && value.endDate) || (!value.endDate && value.startDate));

export const isValidDateRangeAnswer = (
  answer?:
    | FieldAnswerValue
    | ReferenceAnswerValue[]
    | TagAnswerValue
    | AttributeAnswerValue
) => {
  if (!answer) return true;
  if (typeof answer !== 'object' || answer === null || isArray(answer))
    return false;
  return (
    isEmptyDateRangeAnswer(answer) ||
    isPartialDateRangeAnswer(answer) ||
    !isBefore(
      parseDate(answer?.endDate ?? currentDate().toString()),
      parseDate(answer?.startDate ?? currentDate().toString())
    )
  );
};

export const isValidEmailAnswer = (
  possibleEmail?:
    | FieldAnswerValue
    | ReferenceAnswerValue[]
    | TagAnswerValue
    | AttributeAnswerValue
) => {
  if (!possibleEmail) return true;
  if (typeof possibleEmail !== 'string') return false;
  return /\S+@\S+\.\S+/.test(possibleEmail);
};

export const isAnswerRequired = (validators?: SurveyQuestionValidator[]) => {
  return validators?.some(
    validator =>
      validator.type === 'required' ||
      (isRangeValidator(validator) &&
        validator.min !== undefined &&
        validator.min > 0)
  );
};

const findRangeMin = (validators?: SurveyQuestionValidator[]) => {
  if (!validators) {
    return 0;
  }
  let highestMin = 0;

  for (const validator of validators) {
    if (validator.min !== undefined && validator.min > highestMin) {
      highestMin = validator.min;
    }
  }

  return highestMin;
};

const rangeRequirementFulfilled = (
  answer: Answer | undefined,
  question: PersistedTagQuestion | PersistedReferenceQuestion
) => {
  if (!answer) return false;
  if (!(isReferenceAnswer(answer) || isTagAnswer(answer))) return false;

  const validatorMin = findRangeMin(question.validators);
  if (answer.value.length >= validatorMin) return true;

  return false;
};

export const isQuestionAnswered = (
  answer: Answer | undefined,
  question: PersistedSurveyQuestion
) => {
  return isDateRangeQuestion(question) && isDateRangeValue(answer?.value)
    ? !isEmptyDateRangeAnswer(answer?.value)
    : isTagQuestion(question) || isReferenceQuestion(question)
      ? rangeRequirementFulfilled(answer, question)
      : !isEmptyAnswer(answer?.value);
};

export const isConditionAnswer = (answer?: Answer): answer is ConditionAnswer =>
  answer !== undefined && 'answers' in answer;

export const isValidReferenceAnswer = (
  answer?: Answer
): answer is ReferenceAnswer | undefined =>
  !answer || 'totalReferences' in answer;

export const isAPISurveyReferenceAnswer = (
  answer: APISurveyAnswer
): answer is APISurveyReferenceAnswer =>
  answer.type === SurveyQuestionType.REFERENCE;

export const isAPISurveyDependentQuestionAnswer = (
  answer: APISurveyAnswer
): answer is APISurveyDependentQuestionAnswer =>
  'answers' in answer && answer.answers.length > 0;

export const isAPISurveyParentAnswer = (
  answer: APISurveyAnswer
): answer is APISurveyParentAnswer =>
  'parentName' in answer && answer.type === SurveyQuestionType.ATTRIBUTE;

export const isReferenceAnswer = (
  answer?: Answer
): answer is ReferenceAnswer => {
  if (!answer) return false;
  return 'totalReferences' in answer;
};

export const isTagAnswer = (answer: Answer): answer is TagAnswer => {
  if (isReferenceAnswer(answer)) return false;
  return Array.isArray(answer.value);
};
